import { ReactNode, useState } from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";
interface SortableItemProps {
  id: string;
  children: ReactNode;
  disabled?: boolean;
}
const SortableItem = ({
  id,
  children,
  disabled
}: SortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id,
    disabled
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };
  return <div ref={setNodeRef} style={style} className={cn("relative", isDragging && "z-50 opacity-90")}>
      <div className="flex items-start gap-2">
        {!disabled && <button {...attributes} {...listeners} className="mt-6 p-2 cursor-grab hover:bg-muted rounded-md text-muted-foreground hover:text-foreground transition-colors" aria-label="Drag to reorder">
            <Icon name="drag_handle" size={16} />
          </button>}
        <div className="flex-1 py-[24px]">{children}</div>
      </div>
    </div>;
};
interface DraggableListProps<T extends {
  id: string;
}> {
  items: T[];
  onReorder: (items: T[]) => void;
  renderItem: (item: T, index: number) => ReactNode;
  disabled?: boolean;
}
export function DraggableList<T extends {
  id: string;
}>({
  items,
  onReorder,
  renderItem,
  disabled = false
}: DraggableListProps<T>) {
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8
    }
  }), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));
  const handleDragEnd = (event: DragEndEvent) => {
    const {
      active,
      over
    } = event;
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex(item => item.id === active.id);
      const newIndex = items.findIndex(item => item.id === over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);
      onReorder(newItems);
    }
  };
  return <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map(item => item.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-4">
          {items.map((item, index) => <SortableItem key={item.id} id={item.id} disabled={disabled}>
              {renderItem(item, index)}
            </SortableItem>)}
        </div>
      </SortableContext>
    </DndContext>;
}